if (!window.RabariStoryInitialized) {
  window.RabariStoryInitialized = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!reducedMotion.matches && 'IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('rabari-is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .12 });

    const observeReveals = (root) => {
      const sections = root.matches?.('.rabari-section')
        ? [root]
        : Array.from(root.querySelectorAll?.('.rabari-section') || []);
      sections.forEach((section) => {
        section.classList.add('rabari-motion-ready');
        section.querySelectorAll('[data-rabari-reveal]').forEach((element) => revealObserver.observe(element));
      });
    };

    observeReveals(document);
    document.addEventListener('shopify:section:load', (event) => observeReveals(event.target));
  }
}

if (!customElements.get('rabari-carousel')) {
  class RabariCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rabari-track]');
      this.items = Array.from(this.querySelectorAll('[data-rabari-item]'));
      this.dots = Array.from(this.querySelectorAll('[data-rabari-dot]'));
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.interval = Math.max(2000, Number(this.dataset.interval) || 3000);
      this.currentIndex = 0;
      this.isInView = true;

      if (!this.track || this.items.length < 2) return;

      this.createClones();
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index, true)));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.track.addEventListener('pointermove', (event) => this.onPointerMove(event), { passive: false });
      this.track.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.track.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.track.addEventListener('focusin', () => this.stop());
      this.track.addEventListener('focusout', () => this.start());

      this.onViewportChange = () => {
        window.clearTimeout(this.scrollTimer);
        if (this.mobileQuery.matches) {
          requestAnimationFrame(() => {
            this.jumpTo(this.items[this.currentIndex]);
            this.start();
          });
        } else {
          this.stop();
          this.track.scrollLeft = 0;
        }
      };
      this.mobileQuery.addEventListener?.('change', this.onViewportChange);

      this.onVisibilityChange = () => document.hidden ? this.stop() : this.start();
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if ('IntersectionObserver' in window) {
        this.viewportObserver = new IntersectionObserver((entries) => {
          this.isInView = entries[0]?.isIntersecting ?? true;
          this.isInView ? this.start() : this.stop();
        }, { threshold: .12 });
        this.viewportObserver.observe(this);
      }

      this.setCurrent(0);
      if (this.mobileQuery.matches) requestAnimationFrame(() => this.jumpTo(this.items[0]));
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      window.clearTimeout(this.scrollTimer);
      this.viewportObserver?.disconnect();
      this.mobileQuery?.removeEventListener?.('change', this.onViewportChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    createClones() {
      this.firstClone = this.items[0].cloneNode(true);
      this.lastClone = this.items[this.items.length - 1].cloneNode(true);
      [this.firstClone, this.lastClone].forEach((clone) => {
        clone.classList.add('rabari-carousel__clone');
        clone.removeAttribute('data-rabari-item');
        clone.removeAttribute('data-shopify-editor-block');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
      });
      this.track.insertBefore(this.lastClone, this.track.firstChild);
      this.track.appendChild(this.firstClone);
      this.allItems = [this.lastClone, ...this.items, this.firstClone];
    }

    scrollPosition(item) {
      const trackRect = this.track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left - trackRect.left + (itemRect.width / 2);
      return this.track.scrollLeft + itemCenter - (trackRect.width / 2);
    }

    jumpTo(item) {
      if (!item) return;
      this.isJumping = true;
      this.track.classList.add('is-jumping');
      this.track.scrollLeft = this.scrollPosition(item);
      requestAnimationFrame(() => {
        this.track.classList.remove('is-jumping');
        this.isJumping = false;
      });
    }

    setCurrent(index) {
      this.currentIndex = (index + this.items.length) % this.items.length;
      this.dots.forEach((dot, dotIndex) => {
        const current = dotIndex === this.currentIndex;
        dot.classList.toggle('is-current', current);
        dot.setAttribute('aria-current', String(current));
      });
    }

    goTo(index, userInitiated = false) {
      if (!this.mobileQuery.matches) return;
      const normalized = (index + this.items.length) % this.items.length;
      let target = this.items[normalized];
      if (index >= this.items.length) target = this.firstClone;
      if (index < 0) target = this.lastClone;
      this.setCurrent(normalized);
      this.track.scrollTo({
        left: this.scrollPosition(target),
        behavior: this.reduceMotion ? 'auto' : 'smooth'
      });
      if (userInitiated) this.start();
    }

    onScroll() {
      if (!this.mobileQuery.matches || this.isJumping) return;
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        const trackRect = this.track.getBoundingClientRect();
        const trackCenter = trackRect.left + (trackRect.width / 2);
        let closest = this.allItems[0];
        let distance = Infinity;
        this.allItems.forEach((item) => {
          const itemRect = item.getBoundingClientRect();
          const itemCenter = itemRect.left + (itemRect.width / 2);
          const nextDistance = Math.abs(itemCenter - trackCenter);
          if (nextDistance < distance) {
            closest = item;
            distance = nextDistance;
          }
        });
        if (closest === this.firstClone) {
          this.setCurrent(0);
          this.jumpTo(this.items[0]);
        } else if (closest === this.lastClone) {
          this.setCurrent(this.items.length - 1);
          this.jumpTo(this.items[this.items.length - 1]);
        } else {
          this.setCurrent(this.items.indexOf(closest));
        }
      }, 120);
    }

    onPointerDown(event) {
      if (!this.mobileQuery.matches || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.stop();
      this.dragging = true;
      this.dragAxis = null;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.lastX = event.clientX;
      this.startScroll = this.track.scrollLeft;
      this.track.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      const x = event.clientX - this.startX;
      const y = event.clientY - this.startY;
      this.lastX = event.clientX;
      if (!this.dragAxis && (Math.abs(x) > 7 || Math.abs(y) > 7)) {
        this.dragAxis = Math.abs(x) > Math.abs(y) ? 'x' : 'y';
        if (this.dragAxis === 'x') this.track.classList.add('is-dragging');
      }
      if (this.dragAxis !== 'x') return;
      event.preventDefault();
      this.track.scrollLeft = this.startScroll - x;
    }

    onPointerUp(event) {
      if (!this.dragging) return;
      const horizontal = this.dragAxis === 'x';
      const distance = this.lastX - this.startX;
      this.dragging = false;
      this.dragAxis = null;
      this.track.classList.remove('is-dragging');
      if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      if (horizontal && Math.abs(distance) > 42) {
        this.goTo(this.currentIndex + (distance < 0 ? 1 : -1), true);
      } else if (horizontal) {
        this.goTo(this.currentIndex, true);
      } else {
        this.start();
      }
    }

    start() {
      this.stop();
      if (!this.mobileQuery.matches || document.hidden || !this.isInView) return;
      this.timer = window.setInterval(() => this.goTo(this.currentIndex + 1), this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('rabari-carousel', RabariCarousel);
}
